import { useState, useCallback, useRef } from 'react';

const MAX_HISTORY = 30;

/**
 * Undo/redo hook for the Fabric.js canvas.
 * Serializes canvas state as JSON snapshots.
 */
export function useCanvasHistory(fabricRef) {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const historyStack = useRef([]);
  const redoStack = useRef([]);
  const isRestoring = useRef(false);

  const saveState = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas || isRestoring.current) return;

    const json = JSON.stringify(canvas.toJSON());
    historyStack.current.push(json);

    // Cap history
    if (historyStack.current.length > MAX_HISTORY) {
      historyStack.current.shift();
    }

    // Clear redo stack on new action
    redoStack.current = [];
    setCanUndo(historyStack.current.length > 1);
    setCanRedo(false);
  }, [fabricRef]);

  const undo = useCallback(async () => {
    const canvas = fabricRef.current;
    if (!canvas || historyStack.current.length <= 1) return;

    isRestoring.current = true;

    // Move current state to redo
    const current = historyStack.current.pop();
    redoStack.current.push(current);

    // Restore previous state
    const prev = historyStack.current[historyStack.current.length - 1];
    await canvas.loadFromJSON(JSON.parse(prev));
    canvas.renderAll();

    setCanUndo(historyStack.current.length > 1);
    setCanRedo(true);
    isRestoring.current = false;
  }, [fabricRef]);

  const redo = useCallback(async () => {
    const canvas = fabricRef.current;
    if (!canvas || redoStack.current.length === 0) return;

    isRestoring.current = true;

    const next = redoStack.current.pop();
    historyStack.current.push(next);

    await canvas.loadFromJSON(JSON.parse(next));
    canvas.renderAll();

    setCanUndo(true);
    setCanRedo(redoStack.current.length > 0);
    isRestoring.current = false;
  }, [fabricRef]);

  const clearHistory = useCallback(() => {
    historyStack.current = [];
    redoStack.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  return { saveState, undo, redo, canUndo, canRedo, clearHistory };
}
